import React, { useState } from 'react';
import { theme } from '../../styles/theme';

interface LoginScreenProps {
  onLogin: (password: string) => Promise<boolean>;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const success = await onLogin(password);
      if (success) {
        setError('');
      } else {
        setError('Invalid password. Please try again.');
        setPassword('');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.colors.background,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
  };

  const formContainerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.xl,
    padding: theme.spacing.xxl,
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
    boxShadow: theme.shadows.xl,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '1rem',
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xl,
    lineHeight: 1.5,
  };

  const formStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
  };

  const inputStyle: React.CSSProperties = {
    padding: theme.spacing.md,
    fontSize: '1rem',
    borderRadius: theme.borderRadius.md,
    border: `2px solid ${error ? '#ef4444' : theme.colors.border}`,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    outline: 'none',
    transition: `border-color ${theme.transitions.fast}`,
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    color: 'white',
    padding: theme.spacing.md,
    fontSize: '1rem',
    fontWeight: '600',
    borderRadius: theme.borderRadius.md,
    border: 'none',
    cursor: 'pointer',
    transition: `background-color ${theme.transitions.fast}`,
  };

  const errorStyle: React.CSSProperties = {
    color: '#ef4444',
    fontSize: '0.875rem',
    marginTop: theme.spacing.sm,
  };


  return (
    <div style={containerStyle}>
      <div style={formContainerStyle}>
        <h1 style={titleStyle}>🎵 Record Collection</h1>
        <p style={subtitleStyle}>
          Welcome! Please enter the password to browse the vinyl collection.
        </p>
        
        <form onSubmit={handleSubmit} style={formStyle}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            style={inputStyle}
            autoFocus
          />
          
          <button type="submit" style={buttonStyle} disabled={isLoading}>
            {isLoading ? 'Checking...' : 'Access Collection'}
          </button>
          
          {error && <div style={errorStyle}>{error}</div>}
        </form>

        
        <div style={{ 
          marginTop: theme.spacing.lg, 
          fontSize: '0.75rem', 
          color: theme.colors.textMuted 
        }}>
          Scan QR code → Enter password → Browse vinyl records
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;